import { useState, useCallback, useEffect } from 'react';
import { api } from '@expat-ui/shared';
import { getFreshAccessToken } from '../../utils/authUtils';
import { getExchangeRates } from '../../utils/exchangeRates';
import { useCashflows, useProfile, useResidencyCountries, useScenarios } from '../../hooks/useApi';
import { useExchangeRates } from '../../hooks/useExchangeRates';
import {
    ProjectionResult,
    Country,
    Currency,
    GoalFundingSummary,
    ACCOUNT_TYPE_LABELS,
    GOAL_CATEGORY_LABELS,
    CURRENCY_SYMBOLS,
    COUNTRY_CURRENCIES,
    CASHFLOW_CATEGORY_LABELS,
} from '../../types/api.types';
import { formatAmount } from '../../utils/numberFormat';
import styles from './SimulatorPage.module.scss';

const ALL_RETIREMENT_LOCATIONS: { value: Country; label: string }[] = [
    { value: 'INDIA', label: 'India (INR)' },
    { value: 'US', label: 'United States (USD)' },
    { value: 'CAN', label: 'Canada (CAD)' },
];

const DEFAULT_INFLATION: Record<Country, number> = { INDIA: 6, US: 3, CAN: 2.5 };
const DEFAULT_LIFE_EXPECTANCY: Record<Country, number> = { INDIA: 80, US: 83, CAN: 84 };

const SWR_OPTIONS = [3, 3.5, 4, 4.5, 5];

const DONUT_R = 72;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_R;

export function SimulatorPage() {
    const [retirementAge, setRetirementAge] = useState(55);
    const [retirementCountry, setRetirementCountry] = useState<Country>('INDIA');
    const [inflationRate, setInflationRate] = useState(DEFAULT_INFLATION['INDIA']);
    const [lifeExpectancy, setLifeExpectancy] = useState(DEFAULT_LIFE_EXPECTANCY['INDIA']);
    const [extraSpending, setExtraSpending] = useState<number>(0);
    const [selectedSWR, setSelectedSWR] = useState(4);
    const [result, setResult] = useState<ProjectionResult | null>(null);
    const [selectedAge, setSelectedAge] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeChartTab, setActiveChartTab] = useState<'nw' | 'tax'>('nw');

    const { cashflows } = useCashflows();
    const { scenarios } = useScenarios();
    const [selectedScenarioId, setSelectedScenarioId] = useState<string>('');
    const { profile } = useProfile();
    const { countries: residencyCountries, retirementCountry: profileRetirementCountry } = useResidencyCountries();
    const retirementLocations =
        residencyCountries.length > 0
            ? ALL_RETIREMENT_LOCATIONS.filter((l) => residencyCountries.includes(l.value))
            : ALL_RETIREMENT_LOCATIONS;

    useEffect(() => {
        if (profileRetirementCountry) {
            setRetirementCountry(profileRetirementCountry);
            setInflationRate(DEFAULT_INFLATION[profileRetirementCountry]);
            setLifeExpectancy(DEFAULT_LIFE_EXPECTANCY[profileRetirementCountry]);
        }
    }, [profileRetirementCountry]);

    useEffect(() => {
        const defaultScenario = scenarios.find((s) => s.isDefault);
        if (defaultScenario && !selectedScenarioId) {
            setSelectedScenarioId(defaultScenario.scenarioId);
            setRetirementCountry(defaultScenario.country);
            setInflationRate(DEFAULT_INFLATION[defaultScenario.country]);
            setLifeExpectancy(DEFAULT_LIFE_EXPECTANCY[defaultScenario.country]);
        }
    }, [scenarios]);

    const { convertCurrency } = useExchangeRates();
    const baseCurrency: Currency = profile?.baseCurrency ?? 'INR';

    const allRetirementBudgetItems = cashflows.filter(
        (cf) => cf.type === 'EXPENSE' && !cf.isOneTime && cf.isRetirementExpense,
    );
    const retirementBudgetItems = selectedScenarioId
        ? allRetirementBudgetItems.filter(
              (cf) => !cf.retirementScenarioId || cf.retirementScenarioId === selectedScenarioId,
          )
        : allRetirementBudgetItems;
    const ongoingObligations = cashflows.filter(
        (cf) =>
            cf.type === 'EXPENSE' &&
            !cf.isOneTime &&
            !cf.isRetirementExpense &&
            !cf.endsAtRetirement &&
            cf.endAge > retirementAge,
    );
    const retirementCurrency = COUNTRY_CURRENCIES[retirementCountry];
    const retirementBudgetTotal = retirementBudgetItems.reduce(
        (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency),
        0,
    );
    const ongoingObligationsTotal = ongoingObligations.reduce(
        (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency),
        0,
    );
    const allRetirementOneTime = cashflows.filter(
        (cf) => cf.type === 'EXPENSE' && cf.isRetirementExpense && cf.isOneTime,
    );
    const retirementOneTimeExpenses = selectedScenarioId
        ? allRetirementOneTime.filter(
              (cf) => !cf.retirementScenarioId || cf.retirementScenarioId === selectedScenarioId,
          )
        : allRetirementOneTime;
    const retirementOneTimeTitles = retirementOneTimeExpenses
        .map((cf) => cf.label ?? CASHFLOW_CATEGORY_LABELS[cf.category])
        .join(', ');
    const oneTimeEvents = cashflows.filter((cf) => cf.isOneTime);

    const extraSpendingInBase =
        extraSpending > 0 ? convertCurrency(extraSpending, retirementCurrency, baseCurrency) : 0;

    const runSimulation = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');
            const fxRates = await getExchangeRates();
            const data = await api.post<ProjectionResult>(
                '/projection',
                {
                    retirementAge,
                    retirementCountry,
                    inflationRate: inflationRate / 100,
                    lifeExpectancy,
                    fxRates,
                    ...(extraSpending > 0 ? { withdrawalAmount: extraSpending } : {}),
                },
                token,
            );
            setResult(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Simulation failed');
        } finally {
            setLoading(false);
        }
    }, [retirementAge, retirementCountry, inflationRate, lifeExpectancy, extraSpending]);

    const formatCurrency = (amount: number, currency: string) => formatAmount(Math.round(amount), currency as Currency);

    const swrPortfolio = result ? result.wealthAtRetirement : 0;
    const swrAnnualWithdrawal = swrPortfolio * (selectedSWR / 100);

    const successRate = result?.successRate ?? 0;
    const donutOffset = DONUT_CIRCUMFERENCE * (1 - successRate / 100);
    const successColorClass = successRate >= 80 ? styles.good : successRate >= 60 ? styles.moderate : styles.poor;

    return (
        <div className={styles.page}>
            {/* ── Section 1: Control Center ── */}
            <section className={styles.controlCenter}>
                <div className={styles.controlCenterHeader}>
                    <div>
                        <h2 className={styles.sectionTitle}>Control Center</h2>
                        <p className={styles.sectionSubtitle}>
                            Configure your global retirement parameters for instant modeling.
                        </p>
                    </div>
                    <button className={styles.runButton} onClick={runSimulation} disabled={loading}>
                        {loading ? (
                            <>
                                <span className={styles.spinIcon}>↻</span>
                                Running…
                            </>
                        ) : (
                            'Run Simulation'
                        )}
                    </button>
                </div>

                <div className={styles.controlGrid}>
                    {scenarios.length > 0 ? (
                        <div className={styles.sliderGroup}>
                            <label className={styles.sliderLabel}>Retirement Scenario</label>
                            <select
                                className={styles.select}
                                value={selectedScenarioId}
                                onChange={(e) => {
                                    const id = e.target.value;
                                    setSelectedScenarioId(id);
                                    if (id) {
                                        const s = scenarios.find((sc) => sc.scenarioId === id);
                                        if (s) {
                                            setRetirementCountry(s.country);
                                            setInflationRate(DEFAULT_INFLATION[s.country]);
                                            setLifeExpectancy(DEFAULT_LIFE_EXPECTANCY[s.country]);
                                        }
                                    }
                                }}
                            >
                                <option value="">All expenses</option>
                                {scenarios.map((s) => (
                                    <option key={s.scenarioId} value={s.scenarioId}>
                                        {s.name}
                                        {s.isDefault ? ' (Default)' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ) : (
                        <div className={styles.sliderGroup}>
                            <label className={styles.sliderLabel}>Primary Retirement Location</label>
                            <select
                                className={styles.select}
                                value={retirementCountry}
                                onChange={(e) => {
                                    const country = e.target.value as Country;
                                    setRetirementCountry(country);
                                    setInflationRate(DEFAULT_INFLATION[country]);
                                    setLifeExpectancy(DEFAULT_LIFE_EXPECTANCY[country]);
                                }}
                            >
                                {retirementLocations.map((loc) => (
                                    <option key={loc.value} value={loc.value}>
                                        {loc.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className={styles.sliderGroup}>
                        <div className={styles.sliderLabelRow}>
                            <label className={styles.sliderLabel}>Retirement Age</label>
                            <span className={styles.sliderChip}>{retirementAge}</span>
                        </div>
                        <input
                            type="range"
                            className={styles.slider}
                            min={40}
                            max={70}
                            value={retirementAge}
                            onChange={(e) => setRetirementAge(Number(e.target.value))}
                        />
                        <div className={styles.sliderRange}>
                            <span>40</span>
                            <span>70</span>
                        </div>
                    </div>

                    <div className={styles.sliderGroup}>
                        <div className={styles.sliderLabelRow}>
                            <label className={styles.sliderLabel}>Inflation Rate</label>
                            <span className={styles.sliderChip}>{inflationRate}%</span>
                        </div>
                        <input
                            type="range"
                            className={styles.slider}
                            min={1}
                            max={10}
                            step={0.5}
                            value={inflationRate}
                            onChange={(e) => setInflationRate(Number(e.target.value))}
                        />
                        <div className={styles.sliderRange}>
                            <span>1%</span>
                            <span>10%</span>
                        </div>
                    </div>

                    <div className={styles.sliderGroup}>
                        <div className={styles.sliderLabelRow}>
                            <label className={styles.sliderLabel}>Life Expectancy</label>
                            <span className={styles.sliderChip}>{lifeExpectancy} yrs</span>
                        </div>
                        <input
                            type="range"
                            className={styles.slider}
                            min={75}
                            max={100}
                            value={lifeExpectancy}
                            onChange={(e) => setLifeExpectancy(Number(e.target.value))}
                        />
                        <div className={styles.sliderRange}>
                            <span>75</span>
                            <span>100</span>
                        </div>
                    </div>

                    <div className={`${styles.sliderGroup} ${styles.sliderGroupFull}`}>
                        <div className={styles.sliderLabelRow}>
                            <label className={styles.sliderLabel}>Extra Annual Spending</label>
                            <span className={styles.sliderChip}>
                                {extraSpending > 0 ? formatCurrency(extraSpending, retirementCurrency) : 'None'}
                            </span>
                        </div>
                        <input
                            type="range"
                            className={styles.slider}
                            min={0}
                            max={20000000}
                            step={100000}
                            value={extraSpending}
                            onChange={(e) => setExtraSpending(Number(e.target.value))}
                        />
                        <div className={styles.sliderRange}>
                            <span>None</span>
                            <span>2 Cr</span>
                        </div>
                        {extraSpending > 0 && retirementCurrency !== baseCurrency && (
                            <div className={styles.sliderCurrencyNote}>
                                ≈ {formatCurrency(extraSpendingInBase, baseCurrency)} in {baseCurrency}
                            </div>
                        )}
                    </div>

                    <div className={`${styles.sliderGroup} ${styles.sliderGroupFull}`}>
                        <label className={styles.sliderLabel}>
                            Retirement Expenses <span className={styles.labelMeta}>(from Cashflows)</span>
                        </label>
                        <div className={styles.expenseSummary}>
                            {retirementBudgetItems.length === 0 && ongoingObligations.length === 0 ? (
                                <div className={styles.expenseEmpty}>
                                    No retirement expenses set up. Add them in Cashflows.
                                </div>
                            ) : (
                                <>
                                    {retirementBudgetItems.map((cf, i) => (
                                        <div key={`ret-${i}`} className={styles.expenseItem}>
                                            <span className={styles.expenseItemName}>
                                                <span className={styles.expenseItemText}>
                                                    {cf.label || CASHFLOW_CATEGORY_LABELS[cf.category] || cf.category}
                                                    {cf.label && (
                                                        <span className={styles.expenseItemCategory}>
                                                            {CASHFLOW_CATEGORY_LABELS[cf.category] || cf.category}
                                                        </span>
                                                    )}
                                                </span>
                                                <span className={`${styles.expenseBadge} ${styles.retirementBadge}`}>
                                                    Retirement
                                                </span>
                                            </span>
                                            <span>
                                                {formatAmount(
                                                    Math.round(convertCurrency(cf.amount, cf.currency, baseCurrency)),
                                                    baseCurrency,
                                                )}
                                                /mo
                                            </span>
                                        </div>
                                    ))}
                                    {retirementBudgetItems.length > 0 && (
                                        <div className={styles.expenseSubtotal}>
                                            <span>Retirement Budget</span>
                                            <span>
                                                {CURRENCY_SYMBOLS[baseCurrency]}
                                                {(
                                                    (retirementBudgetTotal * 12) /
                                                    (baseCurrency === 'INR' ? 100000 : 1000)
                                                ).toFixed(1)}
                                                {baseCurrency === 'INR' ? 'L' : 'K'}/yr
                                            </span>
                                        </div>
                                    )}
                                    {ongoingObligations.length > 0 && (
                                        <>
                                            {retirementBudgetItems.length > 0 && (
                                                <div className={styles.expenseDivider} />
                                            )}
                                            {ongoingObligations.map((cf, i) => (
                                                <div key={`obl-${i}`} className={styles.expenseItem}>
                                                    <span className={styles.expenseItemName}>
                                                        <span className={styles.expenseItemText}>
                                                            {cf.label ||
                                                                CASHFLOW_CATEGORY_LABELS[cf.category] ||
                                                                cf.category}
                                                            {cf.label && (
                                                                <span className={styles.expenseItemCategory}>
                                                                    {CASHFLOW_CATEGORY_LABELS[cf.category] ||
                                                                        cf.category}
                                                                </span>
                                                            )}
                                                        </span>
                                                        <span
                                                            className={`${styles.expenseBadge} ${styles.obligationBadge}`}
                                                        >
                                                            Until {cf.endAge}
                                                        </span>
                                                    </span>
                                                    <span>
                                                        {formatAmount(
                                                            Math.round(
                                                                convertCurrency(cf.amount, cf.currency, baseCurrency),
                                                            ),
                                                            baseCurrency,
                                                        )}
                                                        /mo
                                                    </span>
                                                </div>
                                            ))}
                                            <div className={styles.expenseSubtotal}>
                                                <span>Ongoing Obligations</span>
                                                <span>
                                                    {CURRENCY_SYMBOLS[baseCurrency]}
                                                    {(
                                                        (ongoingObligationsTotal * 12) /
                                                        (baseCurrency === 'INR' ? 100000 : 1000)
                                                    ).toFixed(1)}
                                                    {baseCurrency === 'INR' ? 'L' : 'K'}/yr
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {error && <p className={styles.error}>{error}</p>}

            {result && (
                <>
                    {/* ── Section 2: Executive Summary ── */}
                    <section className={styles.executiveSummary}>
                        {/* Hero: Success Probability Donut */}
                        <div className={styles.successCard}>
                            <span className={styles.cardLabel}>Success Probability</span>
                            <div className={styles.donutWrap}>
                                <svg className={styles.donutSvg} viewBox="0 0 176 176">
                                    <circle
                                        className={styles.donutTrack}
                                        cx="88"
                                        cy="88"
                                        r={DONUT_R}
                                        fill="transparent"
                                        strokeWidth="12"
                                    />
                                    <circle
                                        className={`${styles.donutFill} ${successColorClass}`}
                                        cx="88"
                                        cy="88"
                                        r={DONUT_R}
                                        fill="transparent"
                                        strokeWidth="12"
                                        strokeDasharray={DONUT_CIRCUMFERENCE}
                                        strokeDashoffset={donutOffset}
                                        strokeLinecap="round"
                                        transform="rotate(-90 88 88)"
                                    />
                                </svg>
                                <div className={styles.donutCenter}>
                                    <span
                                        className={`${styles.donutValue} ${successColorClass}`}
                                        title={`Out of 1,000 simulated market scenarios, ${result.successRate}% sustained your withdrawals through age ${lifeExpectancy}.`}
                                    >
                                        {result.successRate}%
                                    </span>
                                    <span className={`${styles.donutStatus} ${successColorClass}`}>
                                        {result.successRate >= 80
                                            ? 'Stable'
                                            : result.successRate >= 60
                                              ? 'Moderate Risk'
                                              : 'High Risk'}
                                    </span>
                                </div>
                            </div>
                            <div className={styles.insightsMeta}>Monte Carlo Verified — 1,000 simulations</div>
                        </div>

                        {/* Hero: Safe Annual Spend */}
                        <div className={styles.heroSpendCard}>
                            <div className={styles.heroSpendTop}>
                                <span className={styles.heroCardLabel}>Safe Annual Spend</span>
                                <span className={styles.heroCardIcon}>💳</span>
                            </div>
                            <p className={styles.heroSpendValue}>
                                {formatCurrency(result.safeAnnualSpend, result.baseCurrency)}
                            </p>
                            {retirementCurrency !== result.baseCurrency && (
                                <p className={styles.heroSpendAlt}>
                                    ≈{' '}
                                    {formatCurrency(
                                        convertCurrency(
                                            result.safeAnnualSpend,
                                            result.baseCurrency,
                                            retirementCurrency,
                                        ),
                                        retirementCurrency,
                                    )}{' '}
                                    in {retirementCurrency}
                                </p>
                            )}
                            <p className={styles.heroSpendNote}>
                                Extra you can spend yearly beyond planned expenses (90% confidence)
                            </p>
                            {extraSpendingInBase > 0 &&
                                result.safeAnnualSpend > 0 &&
                                (() => {
                                    const pct = Math.round((extraSpendingInBase / result.safeAnnualSpend) * 100);
                                    const cls =
                                        pct < 75
                                            ? styles.comparisonGood
                                            : pct <= 100
                                              ? styles.comparisonCaution
                                              : styles.comparisonOver;
                                    const text =
                                        pct <= 100
                                            ? `Your current plan uses ${pct}% of your safe limit`
                                            : `Your current plan exceeds safe limit by ${pct - 100}%`;
                                    return (
                                        <div className={styles.heroSpendBar}>
                                            <span className={`${styles.heroSpendBarLabel} ${cls}`}>{text}</span>
                                            <div className={styles.heroSpendBarTrack}>
                                                <div
                                                    className={`${styles.heroSpendBarFill} ${cls}`}
                                                    style={{ width: `${Math.min(pct, 100)}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })()}
                        </div>

                        {/* Secondary stacked metrics */}
                        <div className={styles.metricStack}>
                            <div className={styles.metricCard}>
                                <span className={styles.metricLabel}>Current Net Worth</span>
                                <span className={styles.metricValue}>
                                    {formatCurrency(result.startingWealth, result.baseCurrency)}
                                </span>
                                <span className={styles.metricHint}>Today, across all accounts</span>
                            </div>
                            <div className={styles.metricCard}>
                                <span className={styles.metricLabel}>Wealth at Retirement</span>
                                <span className={styles.metricValue}>
                                    {formatCurrency(result.wealthAtRetirement, result.baseCurrency)}
                                </span>
                                <span className={styles.metricHint}>Projected at age {result.retirementAge}</span>
                            </div>
                        </div>
                    </section>

                    {/* ── Section 3: Insights & Projections ── */}
                    <section className={styles.insightsSection}>
                        <div className={styles.sectionHeadingRow}>
                            <h2 className={styles.sectionTitle}>Insights &amp; Projections</h2>
                            <div className={styles.sectionDivider} />
                        </div>

                        {/* Mobile tab bar — hidden on desktop via CSS */}
                        <div className={styles.chartTabBar}>
                            <button
                                className={`${styles.chartTab} ${activeChartTab === 'nw' ? styles.chartTabActive : ''}`}
                                onClick={() => setActiveChartTab('nw')}
                            >
                                Net Worth
                            </button>
                            <button
                                className={`${styles.chartTab} ${activeChartTab === 'tax' ? styles.chartTabActive : ''}`}
                                onClick={() => setActiveChartTab('tax')}
                            >
                                Tax Efficiency
                            </button>
                        </div>

                        <div className={styles.chartsGrid}>
                            {/* Net Worth bar chart */}
                            <div
                                className={`${styles.chartCard} ${activeChartTab !== 'nw' ? styles.chartCardHidden : ''}`}
                            >
                                <div className={styles.chartCardHeader}>
                                    <h3 className={styles.chartCardTitle}>Projected Net Worth Timeline</h3>
                                    <div className={styles.chartLegend}>
                                        <div className={styles.legendItem}>
                                            <div className={`${styles.legendColor} ${styles.normal}`} />
                                            <span>Normal</span>
                                        </div>
                                        <div className={styles.legendItem}>
                                            <div className={`${styles.legendColor} ${styles.retirementColor}`} />
                                            <span>Retirement</span>
                                        </div>
                                        <div className={styles.legendItem}>
                                            <div className={`${styles.legendColor} ${styles.shortfallColor}`} />
                                            <span>Shortfall</span>
                                        </div>
                                        <div className={styles.legendItem}>
                                            <span className={styles.legendIcon}>★</span>
                                            <span>Goal</span>
                                        </div>
                                        <div className={styles.legendItem}>
                                            <span className={`${styles.legendIcon} ${styles.legendIconInfo}`}>◆</span>
                                            <span>One-time Expense</span>
                                        </div>
                                    </div>
                                </div>
                                <p className={styles.chartHint}>
                                    Click any bar to see income, spending and tax detail for that year.
                                </p>
                                <div className={styles.chartContainer}>
                                    {result.yearlyDataPoints.map((dp) => {
                                        const totalNW =
                                            dp.netWorthByCountry.india +
                                            dp.netWorthByCountry.us +
                                            dp.netWorthByCountry.can;
                                        const maxNW = Math.max(
                                            ...result.yearlyDataPoints.map(
                                                (p) =>
                                                    p.netWorthByCountry.india +
                                                    p.netWorthByCountry.us +
                                                    p.netWorthByCountry.can,
                                            ),
                                        );
                                        const height = maxNW > 0 ? (totalNW / maxNW) * 100 : 0;
                                        const isRetirement = dp.age === result.retirementAge;
                                        const hasGoal = (dp.goalEvents?.length ?? 0) > 0;
                                        const goalTitles = dp.goalEvents?.map((g) => g.label).join(', ');
                                        const hasRetirementOneTime =
                                            retirementOneTimeExpenses.length > 0 && isRetirement;
                                        const tooltipParts = [
                                            `Age ${dp.age}: ${formatCurrency(totalNW, result.baseCurrency)}`,
                                        ];
                                        if (hasGoal) tooltipParts.push(`Goal: ${goalTitles}`);
                                        if (hasRetirementOneTime)
                                            tooltipParts.push(`One-time expense: ${retirementOneTimeTitles}`);
                                        return (
                                            <div
                                                key={dp.year}
                                                role="button"
                                                tabIndex={0}
                                                className={[
                                                    styles.bar,
                                                    dp.isShortfallDetected ? styles.shortfall : '',
                                                    isRetirement ? styles.retirement : '',
                                                    hasGoal ? styles.goalMarker : '',
                                                    hasRetirementOneTime ? styles.retirementExpenseMarker : '',
                                                    selectedAge === dp.age ? styles.barSelected : '',
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                                style={{ height: `${Math.max(2, height)}%` }}
                                                title={tooltipParts.join(' — ')}
                                                onClick={() => setSelectedAge(selectedAge === dp.age ? null : dp.age)}
                                                onKeyDown={(e) =>
                                                    e.key === 'Enter' &&
                                                    setSelectedAge(selectedAge === dp.age ? null : dp.age)
                                                }
                                            />
                                        );
                                    })}
                                </div>
                                <div className={styles.chartAxis}>
                                    <span>Now</span>
                                    <span>Retirement ({result.retirementAge})</span>
                                    <span>Age {lifeExpectancy}</span>
                                </div>

                                {selectedAge !== null &&
                                    (() => {
                                        const dp = result.yearlyDataPoints.find((p) => p.age === selectedAge);
                                        if (!dp) return null;
                                        const netIncome = dp.grossIncome - dp.incomeTaxPaid;
                                        const totalNetWithdrawn = Object.values(dp.withdrawalBreakdown).reduce(
                                            (s, v) => s + v,
                                            0,
                                        );
                                        const totalGrossWithdrawn = Object.values(dp.grossWithdrawalByAccount).reduce(
                                            (s, v) => s + v,
                                            0,
                                        );
                                        const totalTaxThisYear = dp.incomeTaxPaid + dp.withdrawalTaxPaid;
                                        const accountEntries = Object.entries(dp.withdrawalBreakdown).filter(
                                            ([, net]) => net > 0,
                                        );
                                        return (
                                            <div className={styles.yearDetail}>
                                                <div className={styles.yearDetailHeader}>
                                                    <span className={styles.yearDetailTitle}>
                                                        Age {dp.age} — {dp.year}
                                                    </span>
                                                    <button
                                                        className={styles.yearDetailClose}
                                                        onClick={() => setSelectedAge(null)}
                                                    >
                                                        ×
                                                    </button>
                                                </div>

                                                {dp.grossIncome > 0 && (
                                                    <div className={styles.yearDetailSection}>
                                                        <div className={styles.yearDetailSectionTitle}>Income</div>
                                                        <div className={styles.yearDetailRow}>
                                                            <span>Gross income</span>
                                                            <span>
                                                                {formatCurrency(dp.grossIncome, result.baseCurrency)}
                                                            </span>
                                                        </div>
                                                        <div className={`${styles.yearDetailRow} ${styles.taxRow}`}>
                                                            <span>Income tax</span>
                                                            <span>
                                                                −{formatCurrency(dp.incomeTaxPaid, result.baseCurrency)}
                                                            </span>
                                                        </div>
                                                        <div className={`${styles.yearDetailRow} ${styles.totalRow}`}>
                                                            <span>Net to portfolio</span>
                                                            <span>
                                                                {formatCurrency(netIncome, result.baseCurrency)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}

                                                {accountEntries.length > 0 && (
                                                    <div className={styles.yearDetailSection}>
                                                        <div className={styles.yearDetailSectionTitle}>
                                                            Withdrawn from accounts
                                                        </div>
                                                        {accountEntries.map(([accountType, net]) => {
                                                            const gross =
                                                                dp.grossWithdrawalByAccount[accountType] ?? net;
                                                            const tax = gross - net;
                                                            return (
                                                                <div
                                                                    key={accountType}
                                                                    className={styles.yearDetailAccountRow}
                                                                >
                                                                    <span className={styles.yearDetailAccountName}>
                                                                        {ACCOUNT_TYPE_LABELS[
                                                                            accountType as keyof typeof ACCOUNT_TYPE_LABELS
                                                                        ] ?? accountType}
                                                                    </span>
                                                                    <span className={styles.yearDetailAccountAmounts}>
                                                                        {formatCurrency(
                                                                            Math.round(net),
                                                                            result.baseCurrency,
                                                                        )}{' '}
                                                                        net
                                                                        {tax > 1 && (
                                                                            <span className={styles.yearDetailTaxHint}>
                                                                                {' '}
                                                                                +{' '}
                                                                                {formatCurrency(
                                                                                    Math.round(tax),
                                                                                    result.baseCurrency,
                                                                                )}{' '}
                                                                                tax
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                        <div
                                                            className={`${styles.yearDetailRow} ${styles.totalRow}`}
                                                            style={{ marginTop: 8 }}
                                                        >
                                                            <span>Gross withdrawn</span>
                                                            <span>
                                                                {formatCurrency(
                                                                    Math.round(totalGrossWithdrawn),
                                                                    result.baseCurrency,
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div className={`${styles.yearDetailRow} ${styles.taxRow}`}>
                                                            <span>Withdrawal tax</span>
                                                            <span>
                                                                −
                                                                {formatCurrency(
                                                                    Math.round(dp.withdrawalTaxPaid),
                                                                    result.baseCurrency,
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div className={`${styles.yearDetailRow} ${styles.totalRow}`}>
                                                            <span>Net spending</span>
                                                            <span>
                                                                {formatCurrency(
                                                                    Math.round(totalNetWithdrawn),
                                                                    result.baseCurrency,
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className={styles.yearDetailTotalTax}>
                                                    <span>Total tax this year</span>
                                                    <span>
                                                        {formatCurrency(
                                                            Math.round(totalTaxThisYear),
                                                            result.baseCurrency,
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })()}
                            </div>

                            {/* Tax line chart */}
                            {result.yearlyDataPoints.some(
                                (dp) =>
                                    dp.projectedTaxByCountry.india +
                                        dp.projectedTaxByCountry.us +
                                        dp.projectedTaxByCountry.can >
                                    0,
                            ) &&
                                (() => {
                                    const pts = result.yearlyDataPoints;
                                    const maxTax = Math.max(
                                        ...pts.map(
                                            (dp) =>
                                                dp.projectedTaxByCountry.india +
                                                dp.projectedTaxByCountry.us +
                                                dp.projectedTaxByCountry.can,
                                        ),
                                        1,
                                    );
                                    const W = 600,
                                        H = 160,
                                        PAD = 8;
                                    const n = pts.length;
                                    const xs = (i: number) => PAD + (i / Math.max(n - 1, 1)) * (W - 2 * PAD);
                                    const ys = (v: number) => H - PAD - (v / maxTax) * (H - 2 * PAD);
                                    const line = (key: 'india' | 'us' | 'can') =>
                                        pts
                                            .map(
                                                (dp, i) =>
                                                    `${i === 0 ? 'M' : 'L'}${xs(i).toFixed(1)},${ys(dp.projectedTaxByCountry[key]).toFixed(1)}`,
                                            )
                                            .join(' ');
                                    const totalByCountry = pts.reduce(
                                        (acc, dp) => ({
                                            india: acc.india + dp.projectedTaxByCountry.india,
                                            us: acc.us + dp.projectedTaxByCountry.us,
                                            can: acc.can + dp.projectedTaxByCountry.can,
                                        }),
                                        { india: 0, us: 0, can: 0 },
                                    );
                                    return (
                                        <div
                                            className={`${styles.chartCard} ${activeChartTab !== 'tax' ? styles.chartCardHidden : ''}`}
                                        >
                                            <div className={styles.chartCardHeader}>
                                                <h3 className={styles.chartCardTitle}>Annual Tax Paid by Country</h3>
                                                <div className={styles.taxLegend}>
                                                    {totalByCountry.india > 0 && (
                                                        <div className={styles.taxLegendItem}>
                                                            <div className={styles.taxDotIndia} />
                                                            <span>India</span>
                                                            <span className={styles.taxLegendAmt}>
                                                                {formatCurrency(
                                                                    Math.round(totalByCountry.india),
                                                                    result.baseCurrency,
                                                                )}{' '}
                                                                lifetime
                                                            </span>
                                                        </div>
                                                    )}
                                                    {totalByCountry.us > 0 && (
                                                        <div className={styles.taxLegendItem}>
                                                            <div className={styles.taxDotUS} />
                                                            <span>US</span>
                                                            <span className={styles.taxLegendAmt}>
                                                                {formatCurrency(
                                                                    Math.round(totalByCountry.us),
                                                                    result.baseCurrency,
                                                                )}{' '}
                                                                lifetime
                                                            </span>
                                                        </div>
                                                    )}
                                                    {totalByCountry.can > 0 && (
                                                        <div className={styles.taxLegendItem}>
                                                            <div className={styles.taxDotCAN} />
                                                            <span>Canada</span>
                                                            <span className={styles.taxLegendAmt}>
                                                                {formatCurrency(
                                                                    Math.round(totalByCountry.can),
                                                                    result.baseCurrency,
                                                                )}{' '}
                                                                lifetime
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <svg viewBox={`0 0 ${W} ${H}`} className={styles.taxSvg}>
                                                {[0.25, 0.5, 0.75, 1].map((f) => (
                                                    <line
                                                        key={f}
                                                        x1={PAD}
                                                        x2={W - PAD}
                                                        y1={ys(maxTax * f).toFixed(1)}
                                                        y2={ys(maxTax * f).toFixed(1)}
                                                        stroke="currentColor"
                                                        strokeOpacity="0.1"
                                                        strokeWidth="1"
                                                    />
                                                ))}
                                                <path
                                                    d={line('india')}
                                                    fill="none"
                                                    stroke="#FF9933"
                                                    strokeWidth="2"
                                                    strokeLinejoin="round"
                                                />
                                                <path
                                                    d={line('us')}
                                                    fill="none"
                                                    stroke="#002366"
                                                    strokeWidth="2"
                                                    strokeLinejoin="round"
                                                />
                                                <path
                                                    d={line('can')}
                                                    fill="none"
                                                    stroke="#D52B1E"
                                                    strokeWidth="2"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            <div className={styles.chartAxis}>
                                                <span>Now</span>
                                                <span>Retirement ({result.retirementAge})</span>
                                                <span>Age {lifeExpectancy}</span>
                                            </div>
                                        </div>
                                    );
                                })()}
                        </div>
                    </section>

                    {/* ── SWR Playground ── */}
                    <section className={styles.swrSection}>
                        <div className={styles.swrHeader}>Safe Withdrawal Rate Playground</div>
                        <div className={styles.swrButtons}>
                            {SWR_OPTIONS.map((swr) => (
                                <button
                                    key={swr}
                                    className={`${styles.swrButton} ${selectedSWR === swr ? styles.active : ''}`}
                                    onClick={() => setSelectedSWR(swr)}
                                >
                                    {swr}%
                                </button>
                            ))}
                        </div>
                        {swrPortfolio > 0 && (
                            <div className={styles.swrInfo}>
                                At {selectedSWR}% SWR, you can withdraw{' '}
                                {formatCurrency(Math.round(swrAnnualWithdrawal), result.baseCurrency)}
                                /yr from your projected retirement portfolio of{' '}
                                {formatCurrency(Math.round(swrPortfolio), result.baseCurrency)}.
                            </div>
                        )}
                    </section>

                    {/* ── Section 4: Plan Details ── */}
                    {(() => {
                        const milestones: { age: number; totalNW: number; yearsIn: number }[] = [];
                        for (let age = result.retirementAge + 5; age <= lifeExpectancy; age += 5) {
                            const dp = result.yearlyDataPoints.find((p) => p.age === age);
                            if (dp) {
                                milestones.push({
                                    age,
                                    totalNW:
                                        dp.netWorthByCountry.india + dp.netWorthByCountry.us + dp.netWorthByCountry.can,
                                    yearsIn: age - result.retirementAge,
                                });
                            }
                        }
                        if (milestones.length === 0 && oneTimeEvents.length === 0) return null;
                        return (
                            <section className={styles.planDetailsSection}>
                                <div className={styles.sectionHeadingRow}>
                                    <h2 className={styles.sectionTitle}>Plan Details</h2>
                                    <div className={styles.sectionDivider} />
                                </div>
                                <div className={styles.planDetailsGrid}>
                                    {milestones.length > 0 && (
                                        <div className={styles.milestonesCard}>
                                            <h3 className={styles.cardTitle}>Wealth Milestones</h3>
                                            <div className={styles.milestonesScroll}>
                                                {milestones.map((m) => (
                                                    <div
                                                        key={m.age}
                                                        className={`${styles.milestoneCard} ${m.totalNW <= 0 ? styles.milestoneDepletedCard : ''}`}
                                                    >
                                                        <span className={styles.milestoneAge}>AGE {m.age}</span>
                                                        <span
                                                            className={`${styles.milestoneValue} ${m.totalNW <= 0 ? styles.poor : ''}`}
                                                        >
                                                            {m.totalNW > 0
                                                                ? formatCurrency(
                                                                      Math.round(m.totalNW),
                                                                      result.baseCurrency,
                                                                  )
                                                                : 'Depleted'}
                                                        </span>
                                                        <span className={styles.milestoneStatus}>
                                                            {m.yearsIn} yr
                                                            {m.yearsIn !== 1 ? 's' : ''} into retirement
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {oneTimeEvents.length > 0 && (
                                        <div className={styles.oneTimeCard}>
                                            <h3 className={styles.cardTitle}>One-time Events</h3>
                                            <div className={styles.oneTimeList}>
                                                {oneTimeEvents.map((cf) => {
                                                    const isIncome = cf.type === 'INCOME';
                                                    const firesAtRetirement = cf.isRetirementExpense;
                                                    const ageLabel = firesAtRetirement
                                                        ? `at retirement (age ${retirementAge})`
                                                        : `at age ${cf.startAge}`;
                                                    return (
                                                        <div key={cf.cashFlowId} className={styles.oneTimeItem}>
                                                            <span
                                                                className={`${styles.oneTimeSign} ${isIncome ? styles.income : styles.expense}`}
                                                            >
                                                                {isIncome ? '+' : '−'}
                                                            </span>
                                                            <span className={styles.oneTimeLabel}>
                                                                {cf.label || CASHFLOW_CATEGORY_LABELS[cf.category]}
                                                                <span className={styles.oneTimeCategory}>
                                                                    {CASHFLOW_CATEGORY_LABELS[cf.category]}
                                                                </span>
                                                            </span>
                                                            <span className={styles.oneTimeAmount}>
                                                                {formatCurrency(cf.amount, cf.currency)}
                                                            </span>
                                                            <span className={styles.oneTimeAge}>{ageLabel}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </section>
                        );
                    })()}

                    {/* ── Section 5: Strategy & Outcomes ── */}
                    {((result.goalFundingSummary?.length ?? 0) > 0 ||
                        (result.withdrawalSummary?.drawdownSequence?.length ?? 0) > 0) && (
                        <section className={styles.strategySection}>
                            <div className={styles.sectionHeadingRow}>
                                <h2 className={styles.sectionTitle}>Strategy &amp; Outcomes</h2>
                                <div className={styles.sectionDivider} />
                            </div>
                            <div className={styles.strategyGrid}>
                                {result.goalFundingSummary && result.goalFundingSummary.length > 0 && (
                                    <div className={styles.goalsCard}>
                                        <h3 className={styles.cardTitle}>Goal Outcomes</h3>
                                        <div className={styles.goalsGrid}>
                                            {result.goalFundingSummary.map((g: GoalFundingSummary) => {
                                                const fillClass =
                                                    g.fundingRate >= 80
                                                        ? ''
                                                        : g.fundingRate >= 50
                                                          ? styles.medium
                                                          : styles.low;
                                                const isFunded = g.fundingRate >= 80;
                                                return (
                                                    <div key={g.goalId} className={styles.goalCard}>
                                                        <div className={styles.goalCardHeader}>
                                                            <span className={styles.goalLabel}>{g.label}</span>
                                                            <span
                                                                className={`${styles.goalStatusBadge} ${isFunded ? styles.funded : styles.atRisk}`}
                                                            >
                                                                {isFunded ? 'On Track' : 'At Risk'}
                                                            </span>
                                                        </div>
                                                        <p className={styles.goalMeta}>
                                                            {GOAL_CATEGORY_LABELS[g.category]} &bull; Age {g.targetAge}{' '}
                                                            &bull; {formatCurrency(g.targetAmount, result.baseCurrency)}
                                                        </p>
                                                        <div className={styles.goalFundingBar}>
                                                            <div
                                                                className={`${styles.goalFundingFill} ${fillClass}`}
                                                                style={{
                                                                    width: `${g.fundingRate}%`,
                                                                }}
                                                            />
                                                        </div>
                                                        <p className={styles.goalFundingLabel}>
                                                            Funded in {g.fundingRate}% of simulations
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {result.withdrawalSummary && result.withdrawalSummary.drawdownSequence.length > 0 && (
                                    <div className={styles.drawdownCard}>
                                        <h3 className={styles.cardTitle}>Recommended Drawdown Strategy</h3>
                                        <p className={styles.drawdownSubtitle}>
                                            Tax-optimized withdrawal sequence for your retirement residency
                                        </p>
                                        <div className={styles.drawdownSteps}>
                                            {result.withdrawalSummary.drawdownSequence.map((entry) => (
                                                <div key={entry.accountType} className={styles.drawdownStep}>
                                                    <span className={styles.stepNumber}>{entry.priority}</span>
                                                    <div className={styles.stepContent}>
                                                        <span className={styles.stepLabel}>{entry.label}</span>
                                                        <span
                                                            className={`${styles.taxBadge} ${
                                                                entry.taxCategory === 'TAX_FREE'
                                                                    ? styles.taxFree
                                                                    : entry.taxCategory === 'CAPITAL_GAINS'
                                                                      ? styles.capitalGains
                                                                      : entry.taxCategory === 'TAX_DEFERRED'
                                                                        ? styles.taxDeferred
                                                                        : styles.highTax
                                                            }`}
                                                        >
                                                            {entry.taxCategory.replace(/_/g, ' ')}
                                                        </span>
                                                    </div>
                                                    <span className={styles.stepRate}>
                                                        {(entry.effectiveTaxRate * 100).toFixed(1)}%
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className={styles.savingsRow}>
                                            <div className={styles.savingsMetric}>
                                                <span className={styles.savingsLabel}>Effective Withdrawal Tax</span>
                                                <span className={styles.savingsValue}>
                                                    {(result.withdrawalSummary.effectiveTaxRate * 100).toFixed(1)}%
                                                </span>
                                            </div>
                                            {result.withdrawalSummary.taxSavedVsNaive > 0 && (
                                                <div className={styles.savingsMetric}>
                                                    <span className={styles.savingsLabel}>Est. Tax Savings</span>
                                                    <span className={`${styles.savingsValue} ${styles.savings}`}>
                                                        {formatCurrency(
                                                            result.withdrawalSummary.taxSavedVsNaive,
                                                            result.baseCurrency,
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {/* ── Footer: Insights Bar ── */}
                    {result.insights && result.insights.length > 0 && (
                        <footer className={styles.insightsBar}>
                            <div className={styles.insightsBadge}>💡</div>
                            <div className={styles.insightsList}>
                                {result.insights.map((insight, idx) => (
                                    <p key={idx} className={styles.insightText}>
                                        {insight}
                                    </p>
                                ))}
                            </div>
                        </footer>
                    )}
                </>
            )}
        </div>
    );
}
